import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RefreshCcw, Volume2, VolumeX, Zap } from "lucide-react";
import SleepingDog from "@/components/SleepingDog";
import Seismogram from "@/components/Seismogram";
import QuakeList from "@/components/QuakeList";
import EpicenterMap from "@/components/EpicenterMap";
import DepthCrossSection from "@/components/DepthCrossSection";
import SleepJournal from "@/components/SleepJournal";
import WindDebug from "@/components/WindDebug";
import { useQuakes } from "@/hooks/useQuakes";
import { playBark } from "@/lib/bark";
import { IDS } from "@/constants/testIds";

const WAKE_THRESHOLD = 6.0;
const WAKE_DURATION_MS = 6500;

const computeWind = (quakes, now, sim) => {
  // Baseline breeze so the coat is never fully still.
  let wind = 0.06;
  for (const q of quakes) {
    if (!q.time || q.magnitude == null) continue;
    const t = new Date(q.time).getTime();
    const hoursAgo = (now - t) / 3_600_000;
    if (hoursAgo < 0 || hoursAgo > 72) continue;
    const decay = Math.exp(-hoursAgo / 8); // half-life-ish
    const contribution = Math.pow(Math.max(0, q.magnitude - 2), 1.6) / 32;
    wind += contribution * decay;
  }
  // Simulated tremor spike -- decays over ~2.5s
  if (sim.active) {
    const age = (now - sim.startedAt) / 1000;
    if (age < 2.8) {
      wind += (sim.magnitude / 6) * Math.exp(-age * 0.9);
    }
  }
  return Math.min(1.35, wind);
};

const Scene = () => {
  const { quakes, latest, max_magnitude, loading, error, fetched_at, refresh } = useQuakes({
    intervalMs: 45_000,
    mmi: 3,
  });

  const [soundOn, setSoundOn] = useState(true);
  const [awake, setAwake] = useState(false);
  const [showWoof, setShowWoof] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [displayWind, setDisplayWind] = useState(0.06);
  const [debugOpen, setDebugOpen] = useState(false);
  const [override, setOverride] = useState({ active: false, value: 0.06 });
  const [contentment, setContentment] = useState(0);
  // Longitude slice picker (interactive cross-section). Inactive by default;
  // when active, the map shows a draggable band that visitors can translate
  // AND rotate to carve any transect through NZ.
  const [slice, setSlice] = useState({
    active: false,
    lng: 174.5,
    lat: -41,
    theta: 0, // radians clockwise from north (0 = N-S line)
    halfWidth: 0.6,
  });

  const windLevelRef = useRef(0.06);
  const awakeRef = useRef(false);
  const wakeTimer = useRef(null);
  const woofTimer = useRef(null);
  const seenWakeQuake = useRef(new Set());
  const simRef = useRef({ active: false, magnitude: 0, startedAt: 0 });

  useEffect(() => {
    awakeRef.current = awake;
  }, [awake]);

  const triggerWake = useCallback(
    (opts = {}) => {
      setAwake(true);
      setShowWoof(true);
      setShakeKey((k) => k + 1);
      if (soundOn) playBark();
      if (wakeTimer.current) clearTimeout(wakeTimer.current);
      if (woofTimer.current) clearTimeout(woofTimer.current);
      wakeTimer.current = setTimeout(() => setAwake(false), opts.duration ?? WAKE_DURATION_MS);
      woofTimer.current = setTimeout(() => setShowWoof(false), 2200);
    },
    [soundOn]
  );

  const fireMag = useCallback(
    (mag) => {
      // Reuse the simulation spike so the wind ramps up naturally
      simRef.current = { active: true, magnitude: mag, startedAt: Date.now() };
      setTimeout(() => {
        simRef.current = { active: false, magnitude: 0, startedAt: 0 };
      }, 3000);
      if (mag >= WAKE_THRESHOLD) {
        triggerWake();
      }
    },
    [triggerWake]
  );

  // Keyboard shortcut: W opens/closes the debug drawer, Esc closes it.
  useEffect(() => {
    const onKey = (e) => {
      const inField = ["INPUT", "TEXTAREA"].includes(e.target?.tagName);
      if (inField) return;
      if (e.key === "w" || e.key === "W") {
        setDebugOpen((v) => !v);
      } else if (e.key === "Escape") {
        setDebugOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Watch new quakes for wake triggers (magnitude >= 6.0)
  useEffect(() => {
    for (const q of quakes) {
      if (q.magnitude != null && q.magnitude >= WAKE_THRESHOLD && !seenWakeQuake.current.has(q.publicID)) {
        seenWakeQuake.current.add(q.publicID);
        // Only fire for events in the last hour to avoid replaying old quakes on load
        if (q.time && Date.now() - new Date(q.time).getTime() < 60 * 60 * 1000) {
          triggerWake();
        }
      }
    }
  }, [quakes, triggerWake]);

  // Continuously update wind level
  useEffect(() => {
    let raf;
    const loop = () => {
      const now = Date.now();
      const auto = computeWind(quakes, now, simRef.current);
      const w = override.active ? override.value : auto;
      windLevelRef.current = w;
      setDisplayWind(w);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [quakes, override]);

  const simulate = () => {
    simRef.current = { active: true, magnitude: 6.5, startedAt: Date.now() };
    triggerWake();
    setTimeout(() => {
      simRef.current = { active: false, magnitude: 0, startedAt: 0 };
    }, 3000);
  };

  const windPct = Math.round(Math.min(100, displayWind * 100));
  const fmtWhen = fetched_at ? new Date(fetched_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

  return (
    <div className="min-h-screen relative paper-grain paper-vignette dog-scene" data-testid={IDS.scene}>
      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-baseline gap-3">
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--ink-2))]">
            Field notes · Vol. I
          </span>
          <span className="mono text-[10px] text-[hsl(var(--ink-mid))]">/ 001</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid={IDS.soundToggle}
            onClick={() => setSoundOn((s) => !s)}
            aria-label={soundOn ? "Mute bark" : "Unmute bark"}
            className="rounded-full border border-[hsl(var(--ink))]/50 h-9 w-9 flex items-center justify-center hover:bg-[hsl(var(--ink))]/5 transition-colors"
          >
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button
            data-testid={IDS.refreshBtn}
            onClick={refresh}
            aria-label="Refresh quake data"
            className="rounded-full border border-[hsl(var(--ink))]/50 h-9 px-3 flex items-center gap-2 hover:bg-[hsl(var(--ink))]/5 transition-colors mono text-[10px] uppercase tracking-widest"
          >
            <RefreshCcw size={13} /> refresh
          </button>
        </div>
      </header>

      {/* Hero title */}
      <section className="relative z-10 px-6 sm:px-10 mt-2 max-w-[1200px]">
        <h1
          data-testid={IDS.title}
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[hsl(var(--ink))]"
        >
          <span className="italic font-light">A dog</span> that sleeps
          <br />
          on the <span className="italic font-light">shoulder</span> of the earth.
        </h1>
        <p className="mt-4 max-w-xl text-[hsl(var(--ink-2))] text-base sm:text-[17px] leading-relaxed">
          Every hair on this drawing is drawn by hand&mdash;and animated by the ground beneath{" "}
          <span className="mono text-sm">Aotearoa · New Zealand</span>. Live data from{" "}
          <a
            href="https://www.geonet.org.nz/"
            target="_blank"
            rel="noreferrer"
            className="underline decoration-[hsl(var(--seismic))] decoration-2 underline-offset-4 hover:text-[hsl(var(--seismic))]"
          >
            GeoNet
          </a>
          . A magnitude&nbsp;
          <span className="mono">6.0</span> will wake him.
        </p>
      </section>

      {/* Main stage */}
      <main className="relative z-10 px-4 sm:px-8 mt-6 pb-24 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-10">
        {/* Dog scene */}
        <div className="relative">
          <div key={shakeKey} className={showWoof ? "shake-hard" : ""}>
            <SleepingDog
              windLevelRef={windLevelRef}
              awakeRef={awakeRef}
              soundOn={soundOn}
              onContentmentChange={setContentment}
            />
          </div>

          {/* WOOF pop */}
          {showWoof && (
            <div
              data-testid={IDS.woof}
              className="woof-anim absolute left-[26%] top-[42%] pointer-events-none"
              style={{ transform: "translate(-50%, -50%)" }}
            >
              <div className="font-display font-extrabold italic text-[hsl(var(--seismic))] text-6xl sm:text-7xl md:text-8xl leading-none tracking-tighter drop-shadow-[2px_3px_0_hsl(var(--ink))]">
                WOOF!
              </div>
            </div>
          )}

          {/* Corner status stamp */}
          <div
            data-testid={IDS.status}
            className="absolute right-3 top-3 sm:right-6 sm:top-6 border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper))]/70 backdrop-blur-[2px] px-3 py-2 mono text-[10px] uppercase tracking-[0.2em]"
          >
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  awake ? "bg-[hsl(var(--seismic))] animate-pulse" : "bg-[hsl(var(--ink))]/60"
                }`}
              />
              <span>{awake ? "Awake" : "Sleeping"}</span>
            </div>
            <div className="text-[hsl(var(--ink-mid))] mt-1">wind {windPct}%</div>
            {contentment > 0.05 && !awake && (
              <div
                data-testid={IDS.contentmentBadge}
                className="flex items-center gap-1 text-[hsl(var(--seismic))] mt-1 fade-in-up"
              >
                <svg width="10" height="10" viewBox="0 0 24 22" aria-hidden>
                  <path
                    d="M 12 20 C -4 8 4 -4 12 6 C 20 -4 28 8 12 20 Z"
                    fill="currentColor"
                  />
                </svg>
                <span>purring {Math.round(contentment * 100)}%</span>
              </div>
            )}
          </div>

          {/* Legend below dog */}
          <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl mx-auto text-center">
            {[
              { label: "M < 3", desc: "gentle breeze" },
              { label: "M 3–5", desc: "coat shivers" },
              { label: "M ≥ 6", desc: "he wakes" },
            ].map((row) => (
              <div key={row.label} className="border-t border-[hsl(var(--ink))]/40 pt-2">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--ink-2))]">{row.label}</div>
                <div className="font-display italic text-sm text-[hsl(var(--ink))]">{row.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          {/* Latest */}
          <div
            data-testid={IDS.latestQuake}
            className="border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper-2))]/60 p-4"
          >
            <div className="flex items-baseline justify-between mb-1">
              <span className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--ink-2))]">
                Latest tremor
              </span>
              <span className="mono text-[10px] text-[hsl(var(--ink-mid))]">as of {fmtWhen}</span>
            </div>
            {latest ? (
              <>
                <div className="flex items-end gap-3">
                  <div
                    data-testid={IDS.maxMag}
                    className={`font-display leading-none text-5xl ${
                      latest.magnitude >= 6 ? "text-[hsl(var(--seismic))]" : "text-[hsl(var(--ink))]"
                    }`}
                  >
                    {latest.magnitude != null ? latest.magnitude.toFixed(1) : "—"}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--ink-mid))] pb-1">
                    Richter
                  </div>
                </div>
                <div className="mt-2 text-sm text-[hsl(var(--ink))]">{latest.locality || "Unknown locality"}</div>
                <div className="mono text-[10px] text-[hsl(var(--ink-mid))] mt-1">
                  {new Date(latest.time).toLocaleString()} · depth {Math.round(latest.depth ?? 0)}km
                </div>
              </>
            ) : (
              <div className="text-sm text-[hsl(var(--ink-mid))]">
                {loading ? "Reading the ground…" : "No recent felt tremor."}
              </div>
            )}
            {error && (
              <div className="mono text-[10px] text-[hsl(var(--seismic))] mt-2">
                signal lost — {error}
              </div>
            )}
          </div>

          <Seismogram windLevelRef={windLevelRef} awakeRef={awakeRef} width={320} height={64} />

          <QuakeList quakes={quakes} loading={loading} />

          <button
            data-testid={IDS.simulateBtn}
            onClick={simulate}
            className="w-full flex items-center justify-center gap-2 border border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--paper))] py-3 mono text-[11px] uppercase tracking-[0.25em] hover:bg-[hsl(var(--seismic))] hover:border-[hsl(var(--seismic))] transition-colors"
          >
            <Zap size={14} /> Simulate M 6.5 tremor
          </button>
          <p className="mono text-[10px] text-[hsl(var(--ink-mid))] leading-relaxed">
            For demonstration. In practice, a real 6.0 arriving from GeoNet&apos;s live feed will wake him too.
          </p>
        </aside>
      </main>

      {/* Epicentre map section */}
      <section className="relative z-10 px-4 sm:px-8 pb-10">
        <div className="max-w-[1200px] mx-auto space-y-6">
          <EpicenterMap quakes={quakes} slice={slice} setSlice={setSlice} />
          <DepthCrossSection quakes={quakes} slice={slice} />
        </div>
      </section>

      {/* Weekly sleep journal */}
      <section className="relative z-10 px-4 sm:px-8 pb-20">
        <div className="max-w-[1200px] mx-auto">
          <SleepJournal liveQuakes={quakes} />
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[hsl(var(--ink))]/30 mt-4 px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--ink-2))]">
          Source: api.geonet.org.nz/quake · MMI ≥ 3 · refreshed every 45s
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid={IDS.windStamp}
            onClick={() => setDebugOpen(true)}
            aria-label="Open wind debug drawer"
            title="Open wind debug drawer (press W)"
            className="mono text-[9px] uppercase tracking-[0.3em] text-[hsl(var(--ink-mid))] hover:text-[hsl(var(--seismic))] transition-colors border border-dashed border-[hsl(var(--ink))]/25 hover:border-[hsl(var(--seismic))]/60 px-2 py-1"
          >
            wind · press w
          </button>
          <div className="font-display italic text-sm text-[hsl(var(--ink))]">
            Let sleeping dogs lie&mdash;until the earth says otherwise.
          </div>
        </div>
      </footer>

      {/* Hidden debug drawer */}
      <WindDebug
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        override={override}
        setOverride={setOverride}
        onTriggerWake={fireMag}
        autoWind={displayWind}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Scene />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
